import React, { useEffect, useState } from 'react';
import useSWRMutation from 'swr/mutation';
import * as Yup from 'yup';
import styled from 'styled-components';
import { ResponseCodes, TwoFactorAuthenticationMethods } from '@tradingblock/types';
import PrimaryButton from '../components/PrimaryButton';
import { VerifyTwoFactorAuthenticationMethodUrl } from '@tradingblock/api';
import { Field, Form, Formik } from 'formik';
import { Loading } from '../../common';
import { TextboxField } from '../../localize/fields/Textbox';
import { ContextTypes } from '../TwoFactorAuthenticationSetup';
import { FormGroup } from '../../basic';
import { validateTwoFactorAuthenticationSecurityCode } from '../../../utilities';
const StyledEnterSecurityCode = styled.div `
  .update-email-btn, .select-diff-phone-btn, .send-new-code-btn {
    color: ${(params) => params.context === ContextTypes.login ? 'white' : 'var(--blue)'}
  }

  .form-group {
    margin-bottom: 0;
  }
`;
;
const EnterSecurityCode = ({ twoFactorAuthenticationMethodToBeVerified, primaryButtonProps, contactInfoToUseForTwoFactorAuthentication, handleNextViewNavigation, fallbackAuthTokenExpiredMsg, genericErrorMsg, handleSelectDifferentPhoneNumber, handleEmailUpdate, rootRestHubUrl, api, handleAuthTokenExpiration, context, allowAbilityToCancel, cancelBtnProps, siteGrp, }) => {
    const { trigger: initiateSmsTwoFactorAuthenticationMethodVerification, error: errorInitiatingSmsTwoFactorAuthenticationMethodVerification, isMutating: initiatingSmsTwoFactorAuthenticationMethodVerification, } = useSWRMutation(`${rootRestHubUrl}/${VerifyTwoFactorAuthenticationMethodUrl}`, () => api.twoFactorAuthentication.initiateSmsMethodVerification({
        twoFaType: twoFactorAuthenticationMethodToBeVerified,
        contact: contactInfoToUseForTwoFactorAuthentication.phoneNumber,
        siteId: siteGrp,
    }));
    const { trigger: initiateEmailTwoFactorAuthenticationMethodVerification, error: errorInitiatingEmailTwoFactorAuthenticationMethodVerification, isMutating: initiatingEmailTwoFactorAuthenticationMethodVerification, } = useSWRMutation(`${rootRestHubUrl}/${VerifyTwoFactorAuthenticationMethodUrl}`, () => api.twoFactorAuthentication.initiateEmailMethodVerification({
        twoFaType: twoFactorAuthenticationMethodToBeVerified,
        contact: contactInfoToUseForTwoFactorAuthentication,
        siteId: siteGrp,
    }));
    const { trigger: finalizeEmailTwoFactorAuthenticationMethodVerification, error: errorFinalizingEmailTwoFactorAuthenticationMethodVerification, isMutating: finalizingEmailTwoFactorAuthenticationMethodVerification, } = useSWRMutation(`${rootRestHubUrl}/${VerifyTwoFactorAuthenticationMethodUrl}`, (_url, { arg }) => api.twoFactorAuthentication.finalizeEmailMethodVerification({
        twoFaType: twoFactorAuthenticationMethodToBeVerified,
        contact: contactInfoToUseForTwoFactorAuthentication,
        twoFaSecurityCode: arg.twoFactorAuthenticationSecurityCode,
        siteId: siteGrp,
    }));
    const { trigger: finalizeSmsTwoFactorAuthenticationMethodVerification, error: errorFinalizingSmsTwoFactorAuthenticationMethodVerification, isMutating: finalizingSmsTwoFactorAuthenticationMethodVerification, } = useSWRMutation(`${rootRestHubUrl}/${VerifyTwoFactorAuthenticationMethodUrl}`, (_url, { arg }) => api.twoFactorAuthentication.finalizeSmsMethodVerification({
        twoFaType: twoFactorAuthenticationMethodToBeVerified,
        twoFaPhoneType: (contactInfoToUseForTwoFactorAuthentication.type),
        plivoUuid: arg.plivoUuid,
        twoFaSecurityCode: arg.twoFactorAuthenticationSecurityCode,
        siteId: siteGrp,
    }));
    const [plivoUuid, setPlivoUuid] = useState('');
    const [errorMsg, setErrorMsg] = useState(null);
    const issueSendingSecurityCodeErrorMsg = 'We encountered an issue sending the security code. Please click the button to request a new code. If the issue persists, please contact Customer Service.';
    const issueSubmittingSecurityCodeErrorMsg = 'There was an issue submitting the security code. Please click the button to request a new code. If the issue persists, please contact Customer Service.';
    const tooManyRequestsErrorMsg = 'You have exceeded the number of requests allowed. Please try again in a few minutes. If the issue persists, please contact Customer Service.';
    const requestSecurityCode = async () => {
        setErrorMsg(null);
        let responseCodeFromRequest = null;
        if (twoFactorAuthenticationMethodToBeVerified === TwoFactorAuthenticationMethods.sms) {
            const { responseCode, payload } = await initiateSmsTwoFactorAuthenticationMethodVerification();
            responseCodeFromRequest = responseCode;
            if (responseCode === 0) {
                const { plivoUuid } = payload;
                if (plivoUuid) {
                    setPlivoUuid(plivoUuid);
                }
                else {
                    setErrorMsg(issueSendingSecurityCodeErrorMsg);
                }
            }
        }
        else if (twoFactorAuthenticationMethodToBeVerified === TwoFactorAuthenticationMethods.email) {
            const { responseCode } = await initiateEmailTwoFactorAuthenticationMethodVerification();
            responseCodeFromRequest = responseCode;
        }
        if (responseCodeFromRequest !== 0) {
            if (responseCodeFromRequest === 200) {
                if (handleAuthTokenExpiration) {
                    handleAuthTokenExpiration();
                }
                else {
                    setErrorMsg(fallbackAuthTokenExpiredMsg);
                }
            }
            else if (responseCodeFromRequest === 127 || responseCodeFromRequest === 143) {
                setErrorMsg(ResponseCodes[responseCodeFromRequest].description);
            }
            else {
                setErrorMsg(issueSendingSecurityCodeErrorMsg);
            }
        }
    };
    useEffect(() => {
        requestSecurityCode();
    }, []);
    useEffect(() => {
        if (errorFinalizingEmailTwoFactorAuthenticationMethodVerification ||
            errorFinalizingSmsTwoFactorAuthenticationMethodVerification ||
            errorInitiatingEmailTwoFactorAuthenticationMethodVerification ||
            errorInitiatingSmsTwoFactorAuthenticationMethodVerification) {
            let responseCode = null;
            if (errorInitiatingEmailTwoFactorAuthenticationMethodVerification &&
                errorInitiatingEmailTwoFactorAuthenticationMethodVerification.response) {
                responseCode = errorInitiatingEmailTwoFactorAuthenticationMethodVerification.response.status;
            }
            else if (errorInitiatingSmsTwoFactorAuthenticationMethodVerification &&
                errorInitiatingSmsTwoFactorAuthenticationMethodVerification.response) {
                responseCode = errorInitiatingSmsTwoFactorAuthenticationMethodVerification.response.status;
            }
            else if (errorFinalizingEmailTwoFactorAuthenticationMethodVerification &&
                errorFinalizingEmailTwoFactorAuthenticationMethodVerification.response) {
                responseCode = errorFinalizingEmailTwoFactorAuthenticationMethodVerification.response.status;
            }
            else if (errorFinalizingSmsTwoFactorAuthenticationMethodVerification &&
                errorFinalizingSmsTwoFactorAuthenticationMethodVerification.response) {
                responseCode = errorFinalizingSmsTwoFactorAuthenticationMethodVerification.response.status;
            }
            if (responseCode === 429) {
                setErrorMsg(tooManyRequestsErrorMsg);
            }
            else {
                setErrorMsg(genericErrorMsg);
            }
        }
    }, [
        errorFinalizingEmailTwoFactorAuthenticationMethodVerification,
        errorFinalizingSmsTwoFactorAuthenticationMethodVerification,
        errorInitiatingEmailTwoFactorAuthenticationMethodVerification,
        errorInitiatingSmsTwoFactorAuthenticationMethodVerification
    ]);
    const onSubmit = async (values) => {
        setErrorMsg(null);
        let responseCodeFromSubmitRequest = null;
        if (twoFactorAuthenticationMethodToBeVerified === TwoFactorAuthenticationMethods.email) {
            const { responseCode } = await finalizeEmailTwoFactorAuthenticationMethodVerification({
                twoFactorAuthenticationSecurityCode: values.securityCode,
            });
            responseCodeFromSubmitRequest = responseCode;
        }
        else if (twoFactorAuthenticationMethodToBeVerified === TwoFactorAuthenticationMethods.sms) {
            const { responseCode } = await finalizeSmsTwoFactorAuthenticationMethodVerification({
                twoFactorAuthenticationSecurityCode: values.securityCode,
                plivoUuid,
            });
            responseCodeFromSubmitRequest = responseCode;
        }
        if (responseCodeFromSubmitRequest === 0) {
            handleNextViewNavigation();
        }
        if (responseCodeFromSubmitRequest !== 0) {
            if (responseCodeFromSubmitRequest === 200) {
                if (handleAuthTokenExpiration) {
                    handleAuthTokenExpiration();
                }
                else {
                    setErrorMsg(fallbackAuthTokenExpiredMsg);
                }
            }
            else {
                if (responseCodeFromSubmitRequest === 129 ||
                    responseCodeFromSubmitRequest === 131 ||
                    responseCodeFromSubmitRequest === 127) {
                    setErrorMsg(ResponseCodes[responseCodeFromSubmitRequest].description);
                }
                else {
                    setErrorMsg(issueSubmittingSecurityCodeErrorMsg);
                }
            }
        }
    };
    return (React.createElement(StyledEnterSecurityCode, { context: context },
        (finalizingEmailTwoFactorAuthenticationMethodVerification ||
            finalizingSmsTwoFactorAuthenticationMethodVerification ||
            initiatingEmailTwoFactorAuthenticationMethodVerification ||
            initiatingSmsTwoFactorAuthenticationMethodVerification)
            && (React.createElement("div", { className: "loading-container" },
                React.createElement(Loading, null))),
        (!finalizingEmailTwoFactorAuthenticationMethodVerification &&
            !finalizingSmsTwoFactorAuthenticationMethodVerification &&
            !initiatingEmailTwoFactorAuthenticationMethodVerification &&
            !initiatingSmsTwoFactorAuthenticationMethodVerification) && (React.createElement(React.Fragment, null,
            React.createElement("p", null,
                "We sent a security code to ",
                twoFactorAuthenticationMethodToBeVerified === TwoFactorAuthenticationMethods.sms ? contactInfoToUseForTwoFactorAuthentication.phoneNumber : contactInfoToUseForTwoFactorAuthentication,
                "."),
            React.createElement(Formik, { validateOnChange: true, validationSchema: Yup
                    .object()
                    .shape({
                    securityCode: Yup
                        .string()
                        .trim()
                        .test('validityCheck', 'Please enter a valid security code.', (securityCodeString) => validateTwoFactorAuthenticationSecurityCode(securityCodeString)),
                }), onSubmit: onSubmit, initialValues: { securityCode: '' } },
                React.createElement(Form, { translate: undefined },
                    React.createElement(FormGroup, null,
                        React.createElement(Field, { component: TextboxField, id: "securityCode" })),
                    React.createElement("div", { className: "mt-1" },
                        React.createElement("button", { type: "button", className: "send-new-code-btn", onClick: requestSecurityCode }, "Resend code"),
                        React.createElement("span", { className: "ml-1 mr-1" }, "|"),
                        twoFactorAuthenticationMethodToBeVerified === TwoFactorAuthenticationMethods.sms && (React.createElement("button", { type: "button", className: "select-diff-phone-btn", onClick: handleSelectDifferentPhoneNumber }, "Change phone number")),
                        twoFactorAuthenticationMethodToBeVerified === TwoFactorAuthenticationMethods.email && (React.createElement("button", { type: "button", className: "update-email-btn", onClick: handleEmailUpdate }, "Change email"))),
                    React.createElement("div", { className: "btn-wrapper" },
                        allowAbilityToCancel && cancelBtnProps && (React.createElement("button", Object.assign({ type: "button" }, Object.assign({}, cancelBtnProps, { className: `${cancelBtnProps.className ? cancelBtnProps.className : ''} mr-3` })), "Cancel")),
                        React.createElement(PrimaryButton, Object.assign({}, primaryButtonProps, { primaryBtnClassName: `${primaryButtonProps.primaryBtnClassName} mb-3 mt-3` }))))),
            errorMsg && React.createElement("p", { className: "error" }, errorMsg)))));
};
export default EnterSecurityCode;
//# sourceMappingURL=EnterSecurityCode.js.map